export interface CsvUserRow {
  email: string;
  displayName: string;
}

/**
 * Parse a CSV string into an array of user rows.
 * Expected columns: Email, Name (header row required).
 * Supports comma and semicolon delimiters.
 */
export function parseCsvUsers(csvText: string): CsvUserRow[] {
  const lines = csvText
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0);

  if (lines.length < 2) {
    throw new Error("CSV must contain a header row and at least one data row.");
  }

  // Detect delimiter (comma or semicolon)
  const header = lines[0];
  const delimiter = header.includes(";") ? ";" : ",";

  const headers = header.split(delimiter).map(h => h.trim().toLowerCase());

  const emailIdx = headers.findIndex(h => h === "email" || h === "e-mail" || h === "mail");
  const nameIdx = headers.findIndex(h => h === "name" || h === "displayname" || h === "display name" || h === "namn");

  if (emailIdx === -1) {
    throw new Error("CSV is missing an 'Email' column. Expected columns: Email, Name");
  }
  if (nameIdx === -1) {
    throw new Error("CSV is missing a 'Name' column. Expected columns: Email, Name");
  }

  const rows: CsvUserRow[] = [];
  const errors: string[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(delimiter).map(c => c.trim());
    const email = cols[emailIdx] || "";
    const displayName = cols[nameIdx] || "";

    if (!email) {
      errors.push(`Row ${i + 1}: missing email`);
      continue;
    }

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push(`Row ${i + 1}: invalid email "${email}"`);
      continue;
    }

    if (!displayName) {
      errors.push(`Row ${i + 1}: missing name for ${email}`);
      continue;
    }

    rows.push({ email, displayName });
  }

  if (errors.length > 0 && rows.length === 0) {
    throw new Error(`All rows had errors:\n${errors.join("\n")}`);
  }

  return rows;
}
